/** Quick-create an itinerary: operator + name → POST /itineraries/ (a name-only
 *  shell; the funnel mints a stub group + itinerary). The rest (days, pricing,
 *  facets, images) is filled in on the detail editor. */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as itinerariesApi from '@/api/itineraries';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { catalogWriteErrors } from '@/api/catalogWriteErrors';

export function ItineraryCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  // A name the server refused goes under Name; anything about the whole request
  // (`detail`, e.g. `itinerary_not_saved`) is the form's own error, under no field.
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setNameError(null);
    setFormError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => itinerariesApi.create({ tour_operator: tenant!.id, name: name.trim() }),
    onSuccess: (itinerary) => {
      snackbar.show('Itinerary created. Add days and details below.', 'success');
      qc.invalidateQueries({ queryKey: ['itineraries'] });
      reset();
      onClose();
      navigate(`/itineraries/${itinerary.id}`);
    },
    onError: (e) => {
      const nameMessage = catalogWriteErrors(e)?.fields.name ?? null;
      setNameError(nameMessage);
      setFormError(nameMessage ? null : e instanceof ConsoleApiError ? e.message : 'Create failed.');
    },
  });

  const canSubmit = tenant !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New itinerary</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={setTenant}
            label="Operator"
            placeholder="Select operator"
          />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)}
            error={!!nameError} helperText={nameError ?? 'Days and details are added on the next screen.'}
          />
          {formError && <Box role="alert" sx={{ color: 'error.main', fontSize: 13 }}>{formError}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          Create itinerary
        </Button>
      </DialogActions>
    </Dialog>
  );
}
